/**
 * Task Service 2.0
 * Clean task management on Firestore for FinTasks
 * Single source of truth for all task operations
 */

import {
  collection,
  addDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { auth, db } from '@/config/firebase';
import { sanitizeForFirestore } from '@/utils/firestoreHelpers';
import { validateTaskInput } from '@/utils/taskValidator';

/**
 * Add a new task for the current user.
 *
 * @param {{
 *   listId?: string,
 *   title: string,
 *   description?: string,
 *   priority?: 'low' | 'medium' | 'high',
 *   dueDate?: string | Date | null,
 *   reminderAt?: string | Date | null,
 *   isFinancialTask?: boolean,
 *   estimatedAmount?: number | string | null,
 *   currency?: string,
 *   tags?: string[],
 * }} input
 */
export async function addTask(input) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated.');
  }

  const { isValid, errors, parsed } = validateTaskInput(input);
  if (!isValid) {
    throw new Error(errors.join(' '));
  }

  // Date helpers
  function toTimestamp(value) {
    if (!value) return null;
    if (value instanceof Date) return Timestamp.fromDate(value);
    if (typeof value === 'string' && value.trim() !== '') {
      return Timestamp.fromDate(new Date(value));
    }
    return null;
  }

  const dueDateTs = toTimestamp(parsed.dueDate);
  const reminderAtTs = toTimestamp(parsed.reminderAt);

  const docData = {
    ownerId: user.uid,
    listId: parsed.listId,

    title: parsed.title,
    description: parsed.description ? String(parsed.description).trim() : null,

    status: 'todo',
    priority: parsed.priority || 'medium',

    dueDate: dueDateTs,
    reminderAt: reminderAtTs,

    isFinancialTask: !!parsed.isFinancialTask,
    estimatedAmount: parsed.estimatedAmount ?? null,
    currency: parsed.currency || 'GBP',

    tags: Array.isArray(parsed.tags) ? parsed.tags : [],

    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    completedAt: null,
  };

  const cleanData = sanitizeForFirestore(docData);

  const colRef = collection(db, 'users', user.uid, 'todoLists', parsed.listId, 'tasks');
  const docRef = await addDoc(colRef, cleanData);

  return {
    id: docRef.id,
    ...cleanData,
  };
}
